import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { getUnitsSoldReport } from '../api/client';
import { getStores } from '../api/client';

const UnitsSoldReport = () => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState(firstDayOfMonth);
  const [endDate, setEndDate] = useState(today);
  const [storeId, setStoreId] = useState('');
  const [stores, setStores] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    getStores().then(setStores).catch(() => {});
  }, []);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const sd = startDate.toISOString().split('T')[0];
      const ed = endDate.toISOString().split('T')[0];
      const result = await getUnitsSoldReport(sd, ed, storeId || null);
      setData(result);
    } catch (err) {
      setError('Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []); // eslint-disable-line

  // Prepare daily chart data: group by date, one bar per store
  const chartData = React.useMemo(() => {
    if (!data?.daily) return [];
    const byDate = {};
    for (const row of data.daily) {
      if (!byDate[row.date]) byDate[row.date] = { date: row.date };
      byDate[row.date][row.store_name] = (byDate[row.date][row.store_name] || 0) + row.units;
    }
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [data]);

  const storeNames = React.useMemo(() => {
    if (!data?.daily) return [];
    return [...new Set(data.daily.map(d => d.store_name))];
  }, [data]);

  const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899'];

  const totalUnits = data?.by_store?.reduce((s, r) => s + r.total_units, 0) || 0;
  const totalTransactions = data?.by_store?.reduce((s, r) => s + r.total_transactions, 0) || 0;
  const totalSales = data?.by_store?.reduce((s, r) => s + r.total_sales_usd, 0) || 0;

  return (
    <div className="p-4">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="form-label">Desde</label>
            <DatePicker
              selected={startDate}
              onChange={setStartDate}
              dateFormat="dd/MM/yyyy"
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Hasta</label>
            <DatePicker
              selected={endDate}
              onChange={setEndDate}
              dateFormat="dd/MM/yyyy"
              className="form-input"
            />
          </div>
          <div>
            <label className="form-label">Tienda</label>
            <select
              value={storeId}
              onChange={e => setStoreId(e.target.value)}
              className="form-input"
            >
              <option value="">Todas las tiendas</option>
              {stores.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <button onClick={fetchReport} className="btn-primary" disabled={loading}>
            {loading ? 'Cargando...' : '🔍 Generar'}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded mb-4">{error}</div>}

      {data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
              <p className="text-sm text-emerald-600 font-medium">Total Unidades Vendidas</p>
              <p className="text-3xl font-bold text-emerald-700">{totalUnits.toLocaleString()}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <p className="text-sm text-purple-600 font-medium">Total Transacciones</p>
              <p className="text-3xl font-bold text-purple-700">{totalTransactions.toLocaleString()}</p>
              {totalTransactions > 0 && totalUnits > 0 && (
                <p className="text-xs text-purple-500 mt-1">
                  {(totalUnits / totalTransactions).toFixed(1)} unidades/transacción
                </p>
              )}
            </div>
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <p className="text-sm text-blue-600 font-medium">Total Ventas</p>
              <p className="text-3xl font-bold text-blue-700">${totalSales.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
              {totalUnits > 0 && (
                <p className="text-xs text-blue-500 mt-1">
                  ${(totalSales / totalUnits).toFixed(2)} USD/unidad
                </p>
              )}
            </div>
          </div>

          {/* Chart */}
          {chartData.length > 0 && storeNames.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <h3 className="font-semibold text-gray-800 mb-4">📈 Unidades Vendidas por Día</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  {storeNames.map((name, i) => (
                    <Bar key={name} dataKey={name} stackId="a" fill={COLORS[i % COLORS.length]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* By store table */}
          <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-gray-800">📊 Resumen por Tienda</h3>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Tienda</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Unidades Vendidas</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Transacciones</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Ventas USD</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">USD / Unidad</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Registros con unidades</th>
                </tr>
              </thead>
              <tbody>
                {data.by_store.map((row, idx) => (
                  <tr key={row.store_id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-3 font-medium text-blue-700">{row.store_name}</td>
                    <td className="px-4 py-3 text-right font-bold text-emerald-700">{row.total_units.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-gray-700">{row.total_transactions.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-gray-700">${row.total_sales_usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    <td className="px-4 py-3 text-right text-gray-700">
                      {row.total_units > 0 ? `$${(row.total_sales_usd / row.total_units).toFixed(2)}` : '—'}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500 text-sm">{row.records_with_units}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Daily detail table */}
          {data.daily.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-gray-800">📅 Detalle Diario</h3>
              </div>
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Fecha</th>
                      <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Tienda</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Unidades</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Transacciones</th>
                      <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Ventas USD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.daily.map((row, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-2 text-gray-700">{row.date}</td>
                        <td className="px-4 py-2 text-blue-700 font-medium">{row.store_name}</td>
                        <td className="px-4 py-2 text-right font-bold text-emerald-700">{row.units > 0 ? row.units.toLocaleString() : <span className="text-gray-400">—</span>}</td>
                        <td className="px-4 py-2 text-right text-gray-700">{row.transactions}</td>
                        <td className="px-4 py-2 text-right text-gray-700">${row.sales_usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UnitsSoldReport;
